import { Link } from "react-router-dom";
import { Phone, MapPin, ArrowRight } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { CtaBand } from "../components/CtaBand";
import { SITE } from "../data/site";

const MD_SERVICES = [
  { name: "Estate Sales", to: "/services/estate-sales", note: "Full-service sales for Maryland estates — staged, priced, advertised, and hosted in the home." },
  { name: "Estate Cleanouts", to: "/services/cleanouts-downsizing", note: "Complete cleanouts for colonials, split-levels, and ranchers — left empty and broom-clean." },
  { name: "Downsizing", to: "/services/cleanouts-downsizing", note: "Patient sorting help when a longtime Maryland home is traded for something smaller." },
  { name: "Packing & Moving Assistance", to: "/services", note: "Careful packing and move-day coordination, room by room." },
  { name: "Donation Pickup & Furniture Removal", to: "/services", note: "Paid pickup and removal of unwanted furniture and household items for a service fee — donatable goods are matched with local charities." },
  { name: "Decluttering & Home Organization", to: "/services", note: "Garages, attics, and basements brought back under control before a listing — or just for breathing room." },
  { name: "Junk & Unwanted-Item Removal", to: "/services", note: "Bulky items and whole-house haul-aways, with donation and recycling considered first." },
  { name: "Hoarding Assistance", to: "/services", note: "Respectful, judgment-free help at your family's pace." },
];

const MD_SITUATIONS = [
  {
    title: "Settling a parent's estate",
    text: "A longtime family home in Bethesda, Rockville, or Silver Spring full of a lifetime of belongings — we handle the sale or cleanout so the family doesn't have to carry it alone.",
  },
  {
    title: "Getting a Maryland home market-ready",
    text: "Agents and sellers in Bowie, Laurel, and Gaithersburg call us to declutter and empty homes before photos and listings — on the timeline the market demands.",
  },
  {
    title: "Downsizing after decades in one house",
    text: "From a Columbia split-level to a Frederick County farmhouse, we help longtime homeowners sort, sell, donate, and move with patience.",
  },
  {
    title: "Managing an estate from out of town",
    text: "When heirs live hours away, a local team matters. We walk the property, quote the work in writing, and handle it start to finish.",
  },
];

const MD_COUNTIES = [
  "Montgomery County",
  "Prince George's County",
  "Howard County",
  "Anne Arundel County",
  "Frederick County",
  "Baltimore County",
];

const MD_COMMUNITIES = [
  "Silver Spring", "Bethesda", "Rockville", "Gaithersburg", "Germantown", "Columbia",
  "Laurel", "Bowie", "Hyattsville", "College Park", "Greenbelt", "Frederick",
];

export default function MarylandPage() {
  usePageMeta(
    "Estate Sales & Cleanouts in Maryland | MacWay Estate Services",
    "Estate sales, estate cleanouts, downsizing services, and judgment-free home help across Maryland — Montgomery, Prince George's, Howard, Anne Arundel, Frederick, and Baltimore counties. Call (301) 500-0128."
  );

  return (
    <main data-testid="maryland-page">
      <section className="bg-forest text-ivory py-20 md:py-28" data-testid="maryland-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <p className="eyebrow-label">Service area · Maryland</p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight leading-[1.08]" data-testid="maryland-headline">
            Estate sales, cleanouts & downsizing across Maryland
          </h1>
          <p className="mt-6 text-lg text-ivory/85 leading-relaxed max-w-2xl">
            From Silver Spring to Frederick, MacWay helps Maryland families through the practical side of big
            transitions — estate sales, estate cleanouts, downsizing, packing and moving help, donation pickups,
            decluttering, junk removal, and respectful hoarding assistance. One local team, one written quote,
            and the house handled.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="btn-brass" data-testid="maryland-quote-cta">Request a Quote</Link>
            <a href={SITE.phoneHref} className="btn-outline-light" data-testid="maryland-call-cta">
              <Phone className="w-4 h-4" /> Call {SITE.phone}
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory" data-testid="maryland-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">What we do in Maryland</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              Every service the house needs
            </h2>
            <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed">
              Most Maryland projects blend two or three of these — an estate sale followed by a cleanout, or
              downsizing help that turns into packing and donation pickups. You describe the situation; we shape
              the plan.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {MD_SERVICES.map((s) => (
              <Link
                key={s.name}
                to={s.to}
                className="group bg-white border border-linen p-7 hover:border-forest/40 hover:shadow-md transition-all duration-300"
                data-testid={`md-service-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="text-xl font-medium text-ink">{s.name}</h3>
                  <ArrowRight className="w-4 h-4 mt-1.5 text-brass shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="mt-2.5 text-sm text-ink/65 leading-relaxed">{s.note}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sand" data-testid="maryland-situations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">Common situations</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              The calls we get from Maryland families
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {MD_SITUATIONS.map((s) => (
              <div key={s.title} className="bg-white border border-linen p-8">
                <h3 className="text-2xl font-medium text-ink">{s.title}</h3>
                <p className="mt-3 text-base text-ink/70 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory" data-testid="maryland-areas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-linen p-10 md:p-14">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div>
                <p className="eyebrow-label">Where we work in Maryland</p>
                <h2 className="mt-4 text-3xl sm:text-4xl font-medium tracking-tight text-ink">
                  Counties & communities we serve
                </h2>
                <p className="mt-4 text-base text-ink/70 leading-relaxed">
                  Our Maryland work centers on the counties below and their surrounding communities. On the edge
                  of the map? Call <a href={SITE.phoneHref} className="font-semibold text-forest hover:underline">{SITE.phone}</a> —
                  we'll tell you honestly whether we can help.
                </p>
              </div>
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h3 className="font-sans text-sm font-bold uppercase tracking-[0.16em] text-forest mb-4">Counties</h3>
                  <div className="flex flex-wrap gap-2.5">
                    {MD_COUNTIES.map((c) => (
                      <span key={c} className="inline-flex items-center gap-2 bg-sand border border-linen px-4 py-2 text-sm font-semibold text-ink/80" data-testid={`md-county-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <MapPin className="w-3.5 h-3.5 text-brass" /> {c}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-sans text-sm font-bold uppercase tracking-[0.16em] text-forest mb-4">Cities & communities</h3>
                  <div className="flex flex-wrap gap-2.5">
                    {MD_COMMUNITIES.map((c) => (
                      <span key={c} className="bg-ivory border border-linen px-4 py-2 text-sm text-ink/70">
                        {c}
                      </span>
                    ))}
                    <span className="px-4 py-2 text-sm text-ink/50 italic">…and nearby areas</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBand
        title="Maryland property on your mind?"
        text="Tell us about the house and the timeline. We'll walk it, talk through the options, and give you a clear written quote — no pressure, no obligation."
      />
    </main>
  );
}
